/* TeeInBlue auto-fill suggestion — v3 inline card (2026-10-04).
   Was: a full-screen modal (#tee_reset_customization, z-index 99999) opened on
   `teeinblue-event-component-injected`, which stacked on top of the Klaviyo popup after a reload.
   Now: snippets/tee-reset-customization.liquid ships a <template>; this script clones it into the theme
   shell `.caf-pdp-custom-panel` (after the progress header, before the app form) when
   window.teeinblue.hasCustomization(productId) is true. Never touches TeeInBlue DOM — the app fills or
   clears its own form through its public API (refillCustomization / resetCustomization).
   States: shown → busy (spinner on the clicked button) → done ("Filled in — check the details below",
   ~2.6s) → collapsed and removed. "Start fresh" and × collapse straight away; typing into a text field hides
   the suggestion (the shopper took over). Shown once per page view, and only for a customization saved on an
   earlier visit (TeeInBlue saves as soon as the shopper starts, so a mid-visit save never triggers it). */
(() => {
  if (window.__cafAutofillInit) return;
  window.__cafAutofillInit = true;

  const doc = document;
  const TEMPLATE_ID = 'caf-autofill-template';
  const PANEL_SELECTOR = '.caf-pdp-custom-panel';
  const DONE_VISIBLE_MS = 2600;
  const COLLAPSE_MS = 320;
  const SPINNER = '<span class="caf-autofill__spin" aria-hidden="true"></span>';

  let state = 'idle'; // idle → shown → busy → done → closed
  let card = null;
  let watchStop = 0;
  // TeeInBlue saves the customization as soon as the shopper picks/types something, so hasCustomization()
  // turns true mid-visit. The card is only for a customization saved on an EARLIER visit: once the shopper
  // has touched the form in this page view, never offer it.
  let touchedForm = false;

  const getProductId = () => window.TeeInBlueCampaign && window.TeeInBlueCampaign.productId;

  const hasSavedCustomization = () => {
    try {
      const id = getProductId();
      return Boolean(id && window.teeinblue && typeof window.teeinblue.hasCustomization === 'function' && window.teeinblue.hasCustomization(id));
    } catch (error) {
      return false;
    }
  };

  const getPanel = () => {
    const panel = doc.querySelector(PANEL_SELECTOR);
    return panel && panel.querySelector('#teeFormInputs') ? panel : null;
  };

  const say = (message) => {
    const live = card && card.querySelector('[data-caf-autofill-live]');
    if (!live) return;
    live.textContent = '';
    window.setTimeout(() => {
      live.textContent = message;
    }, 60);
  };

  const focusAfterCard = () => {
    const title = doc.querySelector(`${PANEL_SELECTOR} .caf-pdp-progress__title`);
    if (title && typeof title.focus === 'function') title.focus({ preventScroll: true });
  };

  const collapse = (message) => {
    if (!card || state === 'closed') return;
    const node = card;
    const hadFocus = node.contains(doc.activeElement);
    state = 'closed';
    if (message) say(message);
    node.classList.add('is-collapsing');
    window.setTimeout(() => {
      node.remove();
      if (hadFocus) focusAfterCard();
    }, COLLAPSE_MS);
  };

  const setBusy = (button) => {
    state = 'busy';
    card.querySelectorAll('[data-caf-autofill]').forEach((control) => {
      control.disabled = true;
    });
    card.setAttribute('aria-busy', 'true');
    const label = button.querySelector('.caf-autofill__label');
    if (label) label.insertAdjacentHTML('beforebegin', SPINNER);
  };

  const showDone = () => {
    if (!card || state === 'closed') return;
    state = 'done';
    const hadFocus = card.contains(doc.activeElement);
    card.removeAttribute('aria-busy');
    card.classList.add('is-done');
    say('Filled in with your last details. Please check them below.');
    if (hadFocus) {
      const done = card.querySelector('.caf-autofill__done');
      if (done) done.focus({ preventScroll: true });
    }
    window.setTimeout(() => collapse(), DONE_VISIBLE_MS);
  };

  const runAction = (action) => {
    const api = window.teeinblue;
    const id = getProductId();
    try {
      if (action === 'refill' && api && typeof api.refillCustomization === 'function') {
        api.refillCustomization(id);
      } else if (action === 'reset' && api && typeof api.resetCustomization === 'function') {
        api.resetCustomization(id);
      }
    } catch (error) {
      collapse();
      return;
    }
    if (action === 'refill') {
      // The app re-renders its fields on the next ticks; show the confirmation once they settle.
      window.setTimeout(showDone, 450);
    } else {
      window.setTimeout(() => collapse('Okay — starting fresh.'), 250);
    }
  };

  const onCardClick = (event) => {
    const button = event.target instanceof Element && event.target.closest('[data-caf-autofill]');
    if (!button || state !== 'shown') return;
    const action = button.dataset.cafAutofill;
    if (action === 'dismiss') {
      collapse();
      return;
    }
    setBusy(button);
    // Let the spinner paint before the (synchronous) app call re-renders the form.
    window.requestAnimationFrame(() => window.setTimeout(() => runAction(action), 40));
  };

  const place = () => {
    const panel = getPanel();
    const template = doc.getElementById(TEMPLATE_ID);
    if (!panel || !template || !template.content || !template.content.firstElementChild) return false;
    card = template.content.firstElementChild.cloneNode(true);
    card.addEventListener('click', onCardClick);
    // TeeInBlue normally restores the saved details into its form on load already → describe that.
    const prefilled = Array.from(panel.querySelectorAll('#teeFormInputs input[type="text"], #teeFormInputs textarea')).some(
      (input) => !input.classList.contains('tee-custom-select__search-input') && input.value.trim() !== '',
    );
    if (prefilled) {
      card.classList.add('is-prefilled');
      card.querySelectorAll('[data-prefilled-text]').forEach((node) => {
        node.textContent = node.dataset.prefilledText;
      });
    }
    const header = panel.querySelector(':scope > .caf-pdp-progress');
    if (header) header.after(card);
    else panel.prepend(card);
    state = 'shown';
    return true;
  };

  const check = () => {
    if (state !== 'idle') return true;
    if (touchedForm) {
      state = 'closed';
      return true;
    }
    if (!hasSavedCustomization()) return false;
    return place();
  };

  ['pointerdown', 'keydown', 'input', 'change'].forEach((type) => {
    doc.addEventListener(
      type,
      (event) => {
        if (!event.isTrusted || !(event.target instanceof Element) || !event.target.closest('#teeFormInputs, #teeFormActions')) return;
        touchedForm = true;
      },
      true,
    );
  });

  // TeeInBlue may inject before or after this deferred script runs, and the CAF shell is created by
  // theme-hotfixes-product.js on its own timers — retry for a short while, then stop.
  const watch = () => {
    if (check()) return;
    watchStop = Date.now() + 15000;
    const details = doc.querySelector('.product__details--product-page') || doc.body;
    const observer = new MutationObserver(() => {
      if (check() || Date.now() > watchStop) observer.disconnect();
    });
    observer.observe(details, { childList: true, subtree: true });
    [400, 1200, 2500, 5000, 9000].forEach((delay) => window.setTimeout(check, delay));
    window.setTimeout(() => observer.disconnect(), 15500);
  };

  // Keep the card next to the progress header if that header is (re)built after us.
  doc.addEventListener('caf:personalize-progress', () => {
    if (!card || state !== 'shown') return;
    const header = card.parentElement && card.parentElement.querySelector(':scope > .caf-pdp-progress');
    if (header && header.nextElementSibling !== card) header.after(card);
  });

  // Typing into a text field by hand = the shopper took over: hide the suggestion (nothing is wiped), so
  // "Use my last details" can no longer overwrite what they just typed. Not for the searchable-dropdown box:
  // the layout shift would scroll the page and TeeInBlue closes its open list on scroll.
  doc.addEventListener(
    'input',
    (event) => {
      if (state !== 'shown' || !event.isTrusted || !(event.target instanceof Element)) return;
      if (event.target.closest('.tee-custom-select')) return;
      if (event.target.closest('#teeFormInputs')) collapse();
    },
    true,
  );

  doc.addEventListener('teeinblue-event-component-injected', check);

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
})();
